import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { authHeaders } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Hidden admin diagnostic page — NOT linked from the navbar/dashboard.
// Reach it by typing the URL directly: `/admin/diagnose/PROD-015630`.
// Purpose: show the back-office team exactly what state a product is in
// (physical batches vs reservations) and a single "Force Demote Now"
// button when phantom allocations need clearing.
export default function ProductDiagnose() {
  const { sku: urlSku } = useParams();
  const [sku, setSku] = useState(urlSku || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [demoting, setDemoting] = useState(false);

  const load = async (ref) => {
    if (!ref) return;
    setLoading(true); setData(null);
    try {
      const res = await axios.get(`${API}/orders/admin/diagnose-stock/${encodeURIComponent(ref)}`,
        { headers: authHeaders() });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (urlSku) load(urlSku); }, [urlSku]);

  const forceDemote = async () => {
    setDemoting(true);
    try {
      const res = await axios.post(
        `${API}/orders/admin/demote-overallocated?product_id=${encodeURIComponent(sku)}`,
        {}, { headers: authHeaders() });
      const d = res.data || {};
      if ((d.lines_demoted || 0) > 0) {
        toast.success(
          `Demoted ${d.lines_demoted} line(s). ${d.fully_demoted_lines || 0} fully released. ${(d.qty_released || 0).toFixed(0)} units back in pool.`,
        );
        await load(sku);
      } else {
        toast.info('No phantom allocations found — nothing to demote.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Demote failed');
    } finally {
      setDemoting(false);
    }
  };

  const phys = data?.physical_stock?.batches_total ?? 0;
  const master = data?.physical_stock?.master_current_stock ?? 0;
  const stockDrift = (Number(master) || 0) - (Number(phys) || 0);
  const reserved = data?.total_reserved ?? 0;
  const overflow = reserved - phys;

  const healMaster = async () => {
    setDemoting(true);
    try {
      const res = await axios.post(
        `${API}/orders/admin/heal-master-stock?lookup=${encodeURIComponent(sku)}`,
        {}, { headers: authHeaders() });
      const d = res.data || {};
      if (d.healed) {
        toast.success(
          `Master healed: ${d.master_before} → ${d.master_after} (Δ ${d.delta > 0 ? '+' : ''}${d.delta})`,
        );
        await load(sku);
      } else {
        toast.info('No drift to heal — master already matches batch sum.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Heal failed');
    } finally {
      setDemoting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="product-diagnose-page">
      <div>
        <h1 className="text-2xl font-bold">Product Diagnostic</h1>
        <p className="text-sm text-slate-600">
          Inspect physical stock vs open reservations for one product.
          Hidden page — back-office use only.
        </p>
      </div>

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-slate-500">SKU or UUID</label>
          <Input value={sku} onChange={(e) => setSku(e.target.value)}
            placeholder="e.g. PROD-015630"
            data-testid="diagnose-sku-input" />
        </div>
        <Button onClick={() => load(sku)} disabled={loading || !sku}
          data-testid="diagnose-lookup-btn">
          {loading ? 'Loading…' : 'Lookup'}
        </Button>
      </div>

      {data && (
        <>
          <Card>
            <CardContent className="p-4 space-y-2">
              <h2 className="font-semibold text-lg">{data.product?.name}</h2>
              <p className="text-xs text-slate-500 font-mono">
                UUID: {data.product?.id} · SKU: {data.product?.product_id || data.product?.sku}
              </p>
              <p className="text-xs text-slate-500">
                Lookup aliases used: <code>{(data.lookup_keys_used || []).join(', ')}</code>
              </p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-3" data-testid="master-vs-batches">
            <Card className="bg-violet-50">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Master Catalogue Stock</p>
                <p className="text-3xl font-bold text-violet-700" data-testid="master-stock-value">{master}</p>
                <p className="text-xs text-slate-500">products.current_stock</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Sum of Batches</p>
                <p className="text-3xl font-bold text-blue-700" data-testid="batches-sum-value">{phys}</p>
                <p className="text-xs text-slate-500">
                  {data.physical_stock?.batch_count} batch row(s)
                </p>
              </CardContent>
            </Card>
            <Card className={Math.abs(stockDrift) > 0.001 ? 'bg-red-50 border-2 border-red-500' : 'bg-emerald-50'}>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Master ↔ Batch Drift</p>
                <p className={`text-3xl font-bold ${Math.abs(stockDrift) > 0.001 ? 'text-red-700' : 'text-emerald-700'}`}
                  data-testid="stock-drift-value">
                  {stockDrift > 0 ? `+${stockDrift}` : stockDrift}
                </p>
                {Math.abs(stockDrift) > 0.001 ? (
                  <Button size="sm" onClick={healMaster} disabled={demoting}
                    data-testid="heal-master-btn"
                    className="mt-2 bg-red-600 hover:bg-red-700 text-white">
                    {demoting ? 'Healing…' : 'Heal: Master = Sum(Batches)'}
                  </Button>
                ) : (
                  <p className="text-xs text-emerald-700 mt-1">✓ Master matches batches</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-blue-50">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Physical Stock (batches)</p>
                <p className="text-3xl font-bold text-blue-700">{phys}</p>
                <p className="text-xs text-slate-500">
                  {data.physical_stock?.batch_count} batch(es)
                </p>
              </CardContent>
            </Card>
            <Card className={overflow > 0 ? 'bg-red-50' : 'bg-emerald-50'}>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Total Reserved</p>
                <p className={`text-3xl font-bold ${overflow > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                  {reserved}
                </p>
                <p className="text-xs text-slate-500">across open orders</p>
              </CardContent>
            </Card>
            <Card className={overflow > 0 ? 'bg-amber-50 border-2 border-amber-500' : 'bg-slate-50'}>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Phantom Overflow</p>
                <p className={`text-3xl font-bold ${overflow > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                  {overflow > 0 ? `+${overflow}` : '0'}
                </p>
                {overflow > 0 ? (
                  <Button size="sm" onClick={forceDemote} disabled={demoting}
                    data-testid="force-demote-btn"
                    className="mt-2 bg-amber-600 hover:bg-amber-700 text-white">
                    {demoting ? 'Releasing…' : 'Force Demote Now'}
                  </Button>
                ) : (
                  <p className="text-xs text-emerald-700 mt-1">✓ Reservations match physical stock</p>
                )}
              </CardContent>
            </Card>
          </div>

          {(data.physical_stock?.batches || []).length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Physical Batches</h3>
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-2 py-1 text-left">Batch #</th>
                      <th className="px-2 py-1 text-right">Stock</th>
                      <th className="px-2 py-1 text-right">MRP</th>
                      <th className="px-2 py-1 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.physical_stock.batches.map((b, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 font-mono text-xs">{b.batch_number}</td>
                        <td className="px-2 py-1 text-right">{b.current_stock}</td>
                        <td className="px-2 py-1 text-right">₹{b.mrp}</td>
                        <td className="px-2 py-1">{b.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {(data.reservations || []).length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">
                  Open Reservations ({data.reservations.length})
                </h3>
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-2 py-1 text-left">Order #</th>
                      <th className="px-2 py-1 text-left">Franchise</th>
                      <th className="px-2 py-1 text-left">Status</th>
                      <th className="px-2 py-1 text-right">Ordered</th>
                      <th className="px-2 py-1 text-right">Allocated</th>
                      <th className="px-2 py-1 text-right">Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reservations.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 font-mono text-xs text-blue-700">{r.order_number}</td>
                        <td className="px-2 py-1">{r.franchise_name}</td>
                        <td className="px-2 py-1">{r.status}</td>
                        <td className="px-2 py-1 text-right">{r.ordered_qty}</td>
                        <td className="px-2 py-1 text-right text-emerald-700">{r.allocated_qty}</td>
                        <td className="px-2 py-1 text-right text-amber-700">{r.pending_qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
